import React, { useState, useMemo } from 'react';
import { FaTools } from 'react-icons/fa';

// Helper to format numbers as Indian Rupee
const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(value);
};

function InflationCalculator() {
  // --- Input States ---
  const [currentCost, setCurrentCost] = useState(100000); // 1 Lakh
  const [years, setYears] = useState(10);
  const [inflationRate, setInflationRate] = useState(6);
  
  // --- Calculation Logic ---
  const futureCost = useMemo(() => {
    // Future Value formula: FV = P * (1 + r)^n
    const P = currentCost;
    const r = inflationRate / 100;
    const n = years;
    
    const futureValue = P * (1 + r) ** n;
    return Math.round(futureValue);

  }, [currentCost, years, inflationRate]);

  return (
    <div className="bg-gray-50 p-6 rounded-lg border">
      <div className="flex items-center mb-6">
        <FaTools className="h-8 w-8 text-blue-600 mr-3" />
        <h3 className="text-2xl font-bold text-gray-900">Inflation Calculator</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        
        {/* --- Column 1: Sliders --- */}
        <div className="space-y-6">
          {/* Current Cost */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Today's Cost</label>
              <span className="text-xl font-bold text-blue-600">{formatCurrency(currentCost)}</span>
            </div>
            <input
              type="range" min="10000" max="10000000" step="10000"
              value={currentCost}
              onChange={(e) => setCurrentCost(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          {/* Number of Years */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Number of Years</label>
              <span className="text-xl font-bold text-blue-600">{years} Years</span>
            </div>
            <input
              type="range" min="1" max="40" step="1"
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          
          {/* Assumed Inflation Rate */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-lg font-medium text-gray-700">Assumed Inflation Rate</label>
              <span className="text-xl font-bold text-blue-600">{inflationRate}%</span>
            </div>
            <input
              type="range" min="4" max="10" step="0.5"
              value={inflationRate}
              onChange={(e) => setInflationRate(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        </div>

        {/* --- Column 2: Results --- */}
        <div className="bg-blue-50 rounded-xl p-6 flex flex-col justify-center items-center">
          <div className="text-center">
            <p className="text-lg text-gray-600">The future cost of {formatCurrency(currentCost)} in {years} years will be:</p>
            <p className="text-5xl font-extrabold text-gray-900 my-2">
              {formatCurrency(futureCost)}
            </p>
          </div>

          <div className="bg-white rounded-lg p-4 mt-6 text-center">
            <p className="text-lg text-gray-600">Your money will lose</p>
            <p className="text-3xl font-bold text-red-600">
              {formatCurrency(futureCost - currentCost)}
            </p>
            <p className="text-lg text-gray-600">of its value.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InflationCalculator;